import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { injectIntl, FormattedMessage } from "react-intl";
import { onThemeChanged } from "../utils/theme";
import classNames from "classnames";

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter";

import { loadGLTF } from "../components/gltf-model-plus";
import { disposeNode, findNode } from "../utils/three-utils";
import {
  ensureAvatarMaterial,
  getAnimationProfile,
  MAT_NAME,
  setAnimationProfileMetadata
} from "../utils/avatar-utils";
import {
  ANIMATION_SELECTIONS,
  buildAvatarCapabilitiesMetadata,
  buildCustomAvatarExportOptions,
  createCustomAvatarPreviewAnimationController,
  exportCustomAvatar,
  exportCustomAvatarAtRest,
  getAvailableAnimationSelections,
  resolveAvatarCapabilities,
  setAvatarCapabilitiesMetadata,
  verifyCustomAvatarBlobExport
} from "../utils/avatar-capabilities";
import { createImageBitmap, disposeImageBitmap } from "../utils/image-bitmap-utils";
import { proxiedUrlFor } from "../utils/media-url-utils";
import { calculateCameraFraming, resetCameraToObject, ContentType } from "../utils/camera-framing-utils";
import styles from "../assets/stylesheets/avatar-preview.scss";

import warningIconUrl from "../assets/images/warning_icon.png";
import warningIcon2xUrl from "../assets/images/warning_icon@2x.png";
import ProgressBar from "./progress-bar";

import { ANIMATIONS_DICTIONARY } from "../utils/custom-utils";

import nextTick from "../utils/next-tick";
import MouseRotate from "../assets/images/icon_mouse_arrow_1.png";
import MouseZoom from "../assets/images/icon_mouse_zoom_in_out_1.png";
import { ReactComponent as Close } from "./icons/Close.svg"; // Assuming SVG component exists
import TWEEN from "@tweenjs/tween.js";
import { Button } from "./input/Button";

import { Dropdown, Label, Option, Spinner } from "@fluentui/react-components";
import { RoomEnvironment } from "../utils/RoomEnvironment";
import {
  createStudioLighting,
  createShadowReceiver,
  enableAvatarShadows,
  disposeStudioLighting,
  getKeyLight,
  createThrottledShadowUpdater
} from "../utils/avatar-lighting";

const isMobile = typeof AFRAME !== "undefined" ? AFRAME.utils.device.isMobile() : false;

const TEXTURE_PROPS = {
  base_map: ["map"],
  emissive_map: ["emissiveMap"],
  normal_map: ["normalMap"],
  orm_map: ["aoMap", "roughnessMap", "metalnessMap"]
};

const previousPreviewAvatarNode = Symbol("previousPreviewAvatarNode");

const ALL_MAPS = Object.keys(TEXTURE_PROPS);

const ZOOM_STATE_FULL_BODY = "fullBody";
const ZOOM_STATE_HEAD = "head";
const ZOOM_STATE_FEET = "feet";
const ZOOM_ANIMATION_DURATION = 750;

function animationSelectionLabel(intl, selection) {
  switch (selection) {
    case ANIMATION_SELECTIONS.EMBEDDED:
      return intl.formatMessage({
        id: "avatar-preview.animationEmbedded",
        defaultMessage: "Embedded"
      });
    case ANIMATION_SELECTIONS.LEGACY_FEMALE:
      return intl.formatMessage({
        id: "avatar-preview.animationLegacyFemale",
        defaultMessage: "Female Bundle"
      });
    case ANIMATION_SELECTIONS.LEGACY_MALE:
      return intl.formatMessage({
        id: "avatar-preview.animationLegacyMale",
        defaultMessage: "Male Bundle"
      });
    default:
      return intl.formatMessage({
        id: "avatar-preview.animationNone",
        defaultMessage: "No animation"
      });
  }
}

function defaultAnimationSelection(root, report) {
  const available = getAvailableAnimationSelections(report);
  if (report.hasDeclaredAnimationSource) {
    return available.includes(report.explicitSelection) ? report.explicitSelection : ANIMATION_SELECTIONS.NONE;
  }
  if (report.recommendedSelection !== ANIMATION_SELECTIONS.NONE && available.includes(report.recommendedSelection)) {
    return report.recommendedSelection;
  }
  if (!report.fullBody) return ANIMATION_SELECTIONS.NONE;

  const preferredLegacy =
    getAnimationProfile(root) === "male" ? ANIMATION_SELECTIONS.LEGACY_MALE : ANIMATION_SELECTIONS.LEGACY_FEMALE;
  if (available.includes(preferredLegacy)) return preferredLegacy;

  const alternateLegacy =
    preferredLegacy === ANIMATION_SELECTIONS.LEGACY_MALE
      ? ANIMATION_SELECTIONS.LEGACY_FEMALE
      : ANIMATION_SELECTIONS.LEGACY_MALE;
  return available.includes(alternateLegacy) ? alternateLegacy : ANIMATION_SELECTIONS.NONE;
}

function createRenderer(canvas, alpha = false, useDevicePixelRatio = true) {
  const context = canvas.getContext("webgl2", {
    alpha,
    depth: true,
    antialias: true,
    premultipliedAlpha: true,
    preserveDrawingBuffer: false,
    powerPreference: "default"
  });

  const renderer = new THREE.WebGLRenderer({ alpha, canvas, context, forceWebVR: true });
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  if (useDevicePixelRatio) {
    renderer.setPixelRatio(window.devicePixelRatio);
  }
  return renderer;
}

const createImageBitmapFromURL = url =>
  fetch(url)
    .then(r => r.blob())
    .then(createImageBitmap);

const ORBIT_ANGLE = new THREE.Euler(-30 * THREE.MathUtils.DEG2RAD, 30 * THREE.MathUtils.DEG2RAD, 0);
const DEFAULT_MARGIN = 1;

export function fitBoxInFrustum(camera, box, center, margin = DEFAULT_MARGIN, forwardAxis = "positive-z") {
  const halfYExtents = Math.max(box.max.y - center.y, center.y - box.min.y);
  const halfVertFOV = THREE.MathUtils.degToRad(camera.fov / 2);
  const distanceToFitHeight = halfYExtents / Math.tan(halfVertFOV);
  const objectDepthFromCenter = Math.max(0, box.max.z - center.z);

  camera.position.set(0, 0, (distanceToFitHeight + objectDepthFromCenter) * margin);
  if (forwardAxis === "negative-z") camera.position.z *= -1;
  camera.position.applyEuler(ORBIT_ANGLE);
  camera.position.add(center);
  camera.lookAt(center);
}

// This function might still be used by the snapshot renderer as a fallback.
function getThemeBackground() {
  return new THREE.Color(0x3a4048);
}

export async function exportAvatar(scene, options) {
  return exportCustomAvatar(scene, options, new GLTFExporter());
}

// custom avatar export metadata is normalized by avatar-capabilities

function legacyPreviewAnimations() {
  const scene = typeof document === "undefined" ? null : document.querySelector("a-scene");
  const root = scene?.object3D;
  const clipsFor = profile => {
    const names =
      profile === "male"
        ? [ANIMATIONS_DICTIONARY.male_anim, ANIMATIONS_DICTIONARY.male_idle_anim]
        : [ANIMATIONS_DICTIONARY.female_anim, ANIMATIONS_DICTIONARY.female_idle_anim];
    const containers = names.map(name => root?.getObjectByName(name));
    if (containers.some(container => !Array.isArray(container?.animations) || container.animations.length === 0)) {
      return null;
    }
    return containers.flatMap(container => container.animations);
  };

  return { female: clipsFor("female"), male: clipsFor("male") };
}

const AvatarPreview = React.forwardRef((props, ref) => {
  const {
    avatarGltfUrl,
    className,
    onGltfLoaded,
    progress,
    avatarCreator,
    isSelector,
    showAnimationProfileSelector,
    onSelect,
    isAvatarListing,
    onClose,
    hasError: propHasError,
    isRefetching,
    children,
    intl,
    contentType = "avatar", // Default to "avatar" for backward compatibility
    preserveCamera = false
  } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentViewportWidth, setCurrentViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const [animationCapabilities, setAnimationCapabilities] = useState(null);
  const [animationSelection, setAnimationSelection] = useState(ANIMATION_SELECTIONS.NONE);

  const canvasDomRef = useRef(null);
  const avatarNodeRef = useRef(null);
  const imageBitmapsRef = useRef({});
  const mountedRef = useRef(false);
  const isFullbodyMaleRef = useRef(false);
  const isFullbodyRef = useRef(false);
  const animationProfileRef = useRef("female");
  const animationCapabilitiesRef = useRef(null);
  const animationSelectionRef = useRef(ANIMATION_SELECTIONS.NONE);
  const previewForwardAxisRef = useRef("positive-z");
  const customAnimationControllerRef = useRef(null);
  const roomEnvironmentRef = useRef(null); // Store RoomEnvironment instance
  const envMapRenderTargetRef = useRef(null); // Store main preview's environment render target
  const studioLightingRef = useRef(null);
  const shadowReceiverRef = useRef(null);
  const updateShadowRef = useRef(null);

  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const loadIdRef = useRef(0);

  const snapshotCanvasRef = useRef(null);
  const snapshotCameraRef = useRef(null);
  const snapshotRendererRef = useRef(null);
  const previewRendererRef = useRef(null);
  const removeThemeChangedListenerRef = useRef(null);
  const mixerRef = useRef(null);
  const idleAnimationActionRef = useRef(null);
  const previewMeshRef = useRef(null);
  const originalMapsRef = useRef({});

  const prevPropsRef = useRef(props);
  const containerRef = useRef(null);

  const prevAvatarGltfUrlRef = useRef(null);

  const currentZoomLevelRef = useRef(ZOOM_STATE_FULL_BODY);
  const isAnimatingZoomRef = useRef(false);
  const headBoneWorldPosRef = useRef(new THREE.Vector3()); // To store head bone's world position for calculations
  const feetBoneWorldPosRef = useRef(new THREE.Vector3());

  const handleResize = useCallback(() => {
    if (
      !canvasDomRef.current ||
      !canvasDomRef.current.parentElement ||
      !previewRendererRef.current ||
      !cameraRef.current
    )
      return;

    const newWidth = window.innerWidth;
    setCurrentViewportWidth(newWidth);

    const parentElement = canvasDomRef.current.parentElement;
    const width = parentElement.offsetWidth;
    const height = parentElement.offsetHeight;

    previewRendererRef.current.setSize(width, height);
    cameraRef.current.aspect = width / height;
    cameraRef.current.updateProjectionMatrix();
  }, []);

  const applyMapToPreview = useCallback((name, image) => {
    if (!previewMeshRef.current || !previewMeshRef.current.material) return;

    if (imageBitmapsRef.current[name]) {
      disposeImageBitmap(imageBitmapsRef.current[name]);
    }
    imageBitmapsRef.current[name] = image;

    TEXTURE_PROPS[name].forEach(prop => {
      const texture = previewMeshRef.current.material[prop];
      if (!texture) return;

      const materialQualitySetting = window.APP?.store?.state?.preferences?.materialQualitySetting;

      if (prop === "normalMap" && materialQualitySetting === "low") return;
      if ((prop === "roughnessMap" || prop === "metalnessMap") && materialQualitySetting !== "high") return;

      texture.image = image;
      texture.needsUpdate = true;
    });
    previewMeshRef.current.material.needsUpdate = true;
  }, []);

  const revertMap = useCallback(name => {
    if (!previewMeshRef.current || !previewMeshRef.current.material || !originalMapsRef.current[name]) return;

    if (imageBitmapsRef.current[name]) {
      disposeImageBitmap(imageBitmapsRef.current[name]);
    }
    delete imageBitmapsRef.current[name];

    originalMapsRef.current[name].forEach((bm, i) => {
      const propName = TEXTURE_PROPS[name][i];
      const texture = previewMeshRef.current.material[propName];
      if (texture) {
        texture.image = bm;
        texture.needsUpdate = true;
      }
    });
    previewMeshRef.current.material.needsUpdate = true;
  }, []);

  const applyMaps = useCallback(
    async (oldProps, newProps) => {
      if (!previewMeshRef.current) return;

      await Promise.all(
        ALL_MAPS.map(mapName => {
          const boundApplyMapToPreview = image => applyMapToPreview(mapName, image);
          if (oldProps[mapName] !== newProps[mapName]) {
            if (newProps[mapName] instanceof File) {
              return createImageBitmap(newProps[mapName]).then(boundApplyMapToPreview);
            } else if (typeof newProps[mapName] === "string" && newProps[mapName]) {
              return createImageBitmapFromURL(newProps[mapName]).then(boundApplyMapToPreview);
            } else {
              return revertMap(mapName);
            }
          }
          return Promise.resolve();
        })
      );
    },
    [applyMapToPreview, revertMap]
  );

  const resetCamera = useCallback(() => {
    if (!avatarNodeRef.current || !cameraRef.current || !snapshotCameraRef.current || !controlsRef.current) return;

    // Map contentType to utility ContentType enum
    const contentTypeMap = {
      avatar: ContentType.AVATAR,
      model: ContentType.MODEL,
      generic: ContentType.GENERIC
    };

    const mappedContentType = contentTypeMap[contentType] || ContentType.AVATAR;

    // Use model-viewer approach for models, traditional approach for avatars
    if (contentType === "avatar") {
      // Traditional avatar framing (legacy behavior)
      const box = new THREE.Box3();
      const center = new THREE.Vector3();

      box.setFromObject(avatarNodeRef.current);
      box.getCenter(center);
      center.y = (box.max.y - box.min.y) * 0.58 + box.min.y;

      fitBoxInFrustum(cameraRef.current, box, center, DEFAULT_MARGIN, previewForwardAxisRef.current);
      fitBoxInFrustum(snapshotCameraRef.current, box, center, 0.7, previewForwardAxisRef.current);

      controlsRef.current.target.copy(center);
      controlsRef.current.update();
    } else {
      // Use model-viewer approach for models: bounding sphere + ideal camera distance
      // This provides better framing for all model sizes
      resetCameraToObject(avatarNodeRef.current, cameraRef.current, controlsRef.current, mappedContentType);

      // Calculate snapshot camera position (tighter framing - 100% instead of 105%)
      calculateCameraFraming(avatarNodeRef.current, snapshotCameraRef.current, {
        contentType: mappedContentType,
        padding: 1.0, // Tighter padding for snapshot (100% of ideal distance)
        applyToCamera: true
      });
    }
  }, [contentType]);

  const runAnimationFullBody = useCallback(
    async (startTime = 0) => {
      const avatarForAnim = avatarNodeRef.current;
      if (
        (showAnimationProfileSelector || animationCapabilitiesRef.current?.hasDeclaredAnimationSource) &&
        avatarForAnim
      ) {
        const controller =
          customAnimationControllerRef.current ||
          (customAnimationControllerRef.current = createCustomAvatarPreviewAnimationController({ nextTick }));
        const selection = animationSelectionRef.current;
        let legacyIdleAnimation = null;
        if (selection === ANIMATION_SELECTIONS.LEGACY_FEMALE || selection === ANIMATION_SELECTIONS.LEGACY_MALE) {
          const aSceneEl = document.querySelector("a-scene");
          const animationName =
            selection === ANIMATION_SELECTIONS.LEGACY_MALE
              ? ANIMATIONS_DICTIONARY.male_idle_anim
              : ANIMATIONS_DICTIONARY.female_idle_anim;
          legacyIdleAnimation = aSceneEl?.object3D?.getObjectByName(animationName)?.animations?.[0];
        }

        const result = await controller.play({
          selection,
          report: animationCapabilitiesRef.current,
          avatar: avatarForAnim,
          legacyIdleAnimation,
          startTime
        });
        if (result.status === "stale") return;

        mixerRef.current = controller.mixer;
        idleAnimationActionRef.current = controller.action;
        avatarForAnim.updateMatrixWorld(true);
        if (sceneRef.current) sceneRef.current.visible = true;
        return;
      }

      if (!isFullbodyRef.current || !sceneRef.current || !avatarForAnim) return;

      const hips = avatarForAnim.getObjectByName("Hips");
      if (!hips) {
        console.warn("Hips bone not found for animation.");
        return;
      }

      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
      }
      mixerRef.current = new THREE.AnimationMixer(hips);
      const legacyMixer = mixerRef.current;

      const aSceneEl = document.querySelector("a-scene");
      if (!aSceneEl) {
        console.warn("A-Frame scene not found for animations.");
        return;
      }

      const animNameKey = isFullbodyMaleRef.current ? "male_idle_anim" : "female_idle_anim";
      const nameAnim = ANIMATIONS_DICTIONARY[animNameKey];
      const animationsContainer = aSceneEl.object3D.getObjectByName(nameAnim);

      if (!animationsContainer || !animationsContainer.animations || animationsContainer.animations.length === 0) {
        console.warn(`Animations for ${nameAnim} not found.`);
        return;
      }

      await nextTick();
      if (!mountedRef.current || mixerRef.current !== legacyMixer) return;

      const idleAnimation = animationsContainer.animations[0];
      const action = legacyMixer.clipAction(idleAnimation);
      action.enabled = true;

      if (startTime > 0) {
        action.time = startTime % idleAnimation.duration;
      }

      action.setLoop(THREE.LoopRepeat, Infinity).play();
      idleAnimationActionRef.current = action;

      await nextTick();
      if (!mountedRef.current || mixerRef.current !== legacyMixer || !sceneRef.current) return;
      avatarForAnim.updateMatrixWorld(true);
      sceneRef.current.visible = true;
    },
    [showAnimationProfileSelector]
  );

  const setAvatar = useCallback(
    async (newAvatarNode, loadId) => {
      if (!newAvatarNode || !sceneRef.current || loadId !== loadIdRef.current) return;

      // Hide new avatar initially to prevent T-pose flash/overlap until animation is ready
      if (preserveCamera) {
        newAvatarNode.visible = false;
      }

      let animationTime = 0;
      if (preserveCamera && idleAnimationActionRef.current) {
        animationTime = idleAnimationActionRef.current.time;
      }

      const oldAvatarNode = avatarNodeRef.current;
      newAvatarNode[previousPreviewAvatarNode] = true;
      avatarNodeRef.current = newAvatarNode;
      sceneRef.current.add(newAvatarNode);

      const shadowsEnabled = window.APP?.store?.state?.preferences?.enableDynamicShadows === true;
      if (shadowsEnabled) {
        enableAvatarShadows(newAvatarNode);
      }

      // Position shadow receiver at avatar's feet
      if (newAvatarNode) {
        const box = new THREE.Box3().setFromObject(newAvatarNode);
        if (shadowReceiverRef.current) {
          shadowReceiverRef.current.position.y = box.min.y + 0.001;
        }
        // Trigger shadow update for new avatar
        if (shadowsEnabled && updateShadowRef.current) {
          updateShadowRef.current();
        }
      }

      await runAnimationFullBody(animationTime);
      if (!mountedRef.current || !sceneRef.current || loadId !== loadIdRef.current) {
        sceneRef.current?.remove(newAvatarNode);
        if (avatarNodeRef.current === newAvatarNode) avatarNodeRef.current = oldAvatarNode;
        newAvatarNode.traverse(disposeNode);
        return;
      }

      if (preserveCamera) {
        newAvatarNode.visible = true;
      }

      if (preserveCamera) {
        sceneRef.current.children
          .filter(
            node => node !== newAvatarNode && Object.prototype.hasOwnProperty.call(node, previousPreviewAvatarNode)
          )
          .forEach(node => sceneRef.current.remove(node));
      }

      // Check if the last part of the url has changed to avoid resetting camera unnecessarily
      const isDifferentAvatar =
        !prevAvatarGltfUrlRef.current ||
        new URL(prevAvatarGltfUrlRef.current).pathname !== new URL(avatarGltfUrl).pathname;

      if (isDifferentAvatar && !preserveCamera) {
        resetCamera();
      }
      setError(null);
      setLoading(false);
    },
    [avatarGltfUrl, preserveCamera, resetCamera, runAnimationFullBody]
  );

  const loadPreviewAvatar = useCallback(
    async (gltfUrl, loadId) => {
      if (!mountedRef.current) return undefined;

      let gltf;
      try {
        gltf = await loadGLTF(gltfUrl, "model/gltf", null, ensureAvatarMaterial);
      } catch (e) {
        console.error("Failed to load avatar preview", e);
        if (mountedRef.current && loadId === loadIdRef.current) {
          setLoading(false);
          setError(true);
        }
        return undefined;
      }

      if (!mountedRef.current || loadId !== loadIdRef.current) return undefined;
      const hasPersistedAnimationSelection = Object.prototype.hasOwnProperty.call(
        gltf.scene.userData?.avatarCapabilities?.animations || {},
        "source"
      );
      if (showAnimationProfileSelector || hasPersistedAnimationSelection) {
        const report = resolveAvatarCapabilities(gltf.scene, gltf.animations || [], {
          legacyAnimations: legacyPreviewAnimations()
        });
        const selection = defaultAnimationSelection(gltf.scene, report);

        animationCapabilitiesRef.current = report;
        previewForwardAxisRef.current = report.humanoid.forwardAxis;
        animationSelectionRef.current = selection;
        isFullbodyMaleRef.current = selection === ANIMATION_SELECTIONS.LEGACY_MALE;
        isFullbodyRef.current = report.fullBody;
        if (!showAnimationProfileSelector) {
          if (selection === ANIMATION_SELECTIONS.LEGACY_FEMALE || selection === ANIMATION_SELECTIONS.LEGACY_MALE) {
            setAnimationProfileMetadata(
              gltf.scene,
              isFullbodyRef.current,
              selection === ANIMATION_SELECTIONS.LEGACY_MALE ? "male" : "female"
            );
          } else {
            delete gltf.scene.userData.animationProfile;
            delete gltf.scene.userData.isMaleAnim;
            delete gltf.scene.userData.isFemaleAnim;
          }
        }
        if (showAnimationProfileSelector) {
          setAnimationCapabilities(report);
          setAnimationSelection(selection);
        }
      } else {
        previewForwardAxisRef.current = "positive-z";
        animationCapabilitiesRef.current = null;
        animationSelectionRef.current = null;
        isFullbodyRef.current = Boolean(
          gltf.scene?.getObjectByName("LeftFoot") || gltf.scene?.getObjectByName("RightFoot")
        );
        animationProfileRef.current = isFullbodyRef.current ? getAnimationProfile(gltf.scene) : "female";
        isFullbodyMaleRef.current = animationProfileRef.current === "male";
      }

      if (!isFullbodyRef.current) {
        if (gltf.scene.getObjectByName("Wolf3D_Avatar")) {
          const leftHand = gltf.scene.getObjectByName("LeftHand");
          const rightHand = gltf.scene.getObjectByName("RightHand");
          const HIDDEN_SCALE = 0.00000001;
          if (leftHand) leftHand.scale.setScalar(HIDDEN_SCALE);
          if (rightHand) rightHand.scale.setScalar(HIDDEN_SCALE);
        }
      }

      previewMeshRef.current = findNode(
        gltf.scene,
        n => (n.isMesh && n.material && n.material.name === MAT_NAME) || n.name === "Bot_Skinned"
      );

      // previewMeshRef.current.material.map.colorSpace = THREE.SRGBColorSpace;

      if (!previewMeshRef.current) {
        console.error("Failed to find avatar preview mesh");
        if (mountedRef.current) {
          setLoading(false);
          setError(true);
        }
        return undefined;
      }

      if (mixerRef.current && !preserveCamera) {
        mixerRef.current.stopAllAction();
        mixerRef.current = null;
      }
      if (!preserveCamera) {
        idleAnimationActionRef.current = null;
      }

      const idleEyesAnimation = gltf.animations && gltf.animations.find(({ name }) => name === "idle_eyes");
      if (idleEyesAnimation) {
        mixerRef.current = new THREE.AnimationMixer(gltf.scene);
        const action = mixerRef.current.clipAction(idleEyesAnimation);
        action.enabled = true;
        action.setLoop(THREE.LoopRepeat, Infinity).play();
        idleAnimationActionRef.current = action;
      } else {
        mixerRef.current = null;
      }

      gltf.scene.traverse(node => {
        if (node.isSkinnedMesh) node.frustumCulled = false;
        if (node.material) {
          const removeOnUpdate = p => node.material[p] && delete node.material[p].onUpdate;
          TEXTURE_PROPS["base_map"].forEach(removeOnUpdate);
          TEXTURE_PROPS["emissive_map"].forEach(removeOnUpdate);
          TEXTURE_PROPS["normal_map"].forEach(removeOnUpdate);
          TEXTURE_PROPS["orm_map"].forEach(removeOnUpdate);
        }
      });

      const { material } = previewMeshRef.current;
      if (material) {
        const getImage = p => material[p] && material[p].image;
        originalMapsRef.current = {
          base_map: TEXTURE_PROPS["base_map"].map(getImage),
          emissive_map: TEXTURE_PROPS["emissive_map"].map(getImage),
          normal_map: TEXTURE_PROPS["normal_map"].map(getImage),
          orm_map: TEXTURE_PROPS["orm_map"].map(getImage)
        };

        const currentMapProps = {
          base_map: props.base_map,
          emissive_map: props.emissive_map,
          normal_map: props.normal_map,
          orm_map: props.orm_map
        };
        const dependencies = [applyMaps({}, currentMapProps)];

        if (envMapRenderTargetRef.current && !preserveCamera) {
          envMapRenderTargetRef.current.dispose();
          envMapRenderTargetRef.current = null;
        }

        if (!roomEnvironmentRef.current) {
          roomEnvironmentRef.current = new RoomEnvironment();
        }

        if (!envMapRenderTargetRef.current) {
          const pmremGenerator = new THREE.PMREMGenerator(previewRendererRef.current);
          const newRenderTarget = pmremGenerator.fromScene(roomEnvironmentRef.current);
          sceneRef.current.environment = newRenderTarget.texture;
          envMapRenderTargetRef.current = newRenderTarget;
          sceneRef.current.environmentIntensity = 0.9;
          pmremGenerator.dispose();
        }

        await Promise.all(dependencies);
      } else {
        originalMapsRef.current = {};
      }

      return gltf;
    },
    [
      applyMaps,
      props.base_map,
      props.emissive_map,
      props.normal_map,
      props.orm_map,
      preserveCamera,
      showAnimationProfileSelector
    ]
  );

  const loadCurrentAvatarGltfUrl = useCallback(async () => {
    if (!mountedRef.current) return;

    if (!avatarGltfUrl) {
      ++loadIdRef.current;
      isFullbodyRef.current = false;
      isFullbodyMaleRef.current = false;
      animationProfileRef.current = "female";
      animationCapabilitiesRef.current = null;
      animationSelectionRef.current = ANIMATION_SELECTIONS.NONE;
      setAnimationCapabilities(null);
      setAnimationSelection(ANIMATION_SELECTIONS.NONE);
      return;
    }

    const newLoadId = ++loadIdRef.current;
    const url = proxiedUrlFor(avatarGltfUrl);
    const gltf = await loadPreviewAvatar(url, newLoadId);

    if (!mountedRef.current || newLoadId !== loadIdRef.current) return;

    if (!gltf) {
      isFullbodyRef.current = false;
      isFullbodyMaleRef.current = false;
      animationProfileRef.current = "female";
      animationCapabilitiesRef.current = null;
      animationSelectionRef.current = ANIMATION_SELECTIONS.NONE;
      setAnimationCapabilities(null);
      setAnimationSelection(ANIMATION_SELECTIONS.NONE);
      return;
    }

    if (onGltfLoaded) onGltfLoaded(gltf);
    await setAvatar(gltf.scene, newLoadId);
    if (!mountedRef.current || newLoadId !== loadIdRef.current) return;
    prevAvatarGltfUrlRef.current = avatarGltfUrl;
  }, [avatarGltfUrl, onGltfLoaded, loadPreviewAvatar, setAvatar]);

  const animateCameraAndTarget = useCallback((targetCameraPosition, targetControlsTarget, onCompleteCallback) => {
    if (!cameraRef.current || !controlsRef.current || !mountedRef.current) return;

    isAnimatingZoomRef.current = true;

    // Animate Camera Position
    const currentCamPos = cameraRef.current.position.clone();
    new TWEEN.Tween(currentCamPos)
      .to(targetCameraPosition, ZOOM_ANIMATION_DURATION)
      .easing(TWEEN.Easing.Quadratic.Out)
      .onUpdate(() => {
        if (!mountedRef.current) return;
        cameraRef.current.position.copy(currentCamPos);
      })
      .start();

    // Animate Controls Target
    const currentCtrlTarget = controlsRef.current.target.clone();
    new TWEEN.Tween(currentCtrlTarget)
      .to(targetControlsTarget, ZOOM_ANIMATION_DURATION)
      .easing(TWEEN.Easing.Quadratic.Out)
      .onUpdate(() => {
        if (!mountedRef.current) return;
        controlsRef.current.target.copy(currentCtrlTarget);
        controlsRef.current.update();
      })
      .onComplete(() => {
        if (!mountedRef.current) return;
        isAnimatingZoomRef.current = false;
        if (controlsRef.current) {
          controlsRef.current.saveState();
        }
        if (onCompleteCallback) {
          onCompleteCallback();
        }
      })
      .start();
  }, []);

  const zoomToHead = useCallback(() => {
    if (
      !mountedRef.current ||
      !avatarNodeRef.current ||
      !cameraRef.current ||
      !controlsRef.current ||
      isAnimatingZoomRef.current
    )
      return;
    if (currentZoomLevelRef.current === ZOOM_STATE_HEAD && !isAnimatingZoomRef.current) return;

    const headBone = findNode(avatarNodeRef.current, n => n.isBone && (n.name === "Head" || n.name === "head"));
    if (!headBone) {
      console.warn("Head bone not found for zoom.");
      return;
    }

    headBone.getWorldPosition(headBoneWorldPosRef.current);
    const newControlsTarget = headBoneWorldPosRef.current.clone();
    // Optional: Slightly raise the target for better framing (e.g., eye level instead of chin)
    newControlsTarget.y += 0.05;

    // Calculate target camera position:
    // Maintain current viewing direction relative to the *new* target, but at a specific distance.
    const currentCameraPos = cameraRef.current.position.clone();
    // If camera and new target are very close, direction vector might be unstable.
    const directionToCurrentCamera = new THREE.Vector3().subVectors(currentCameraPos, newControlsTarget);

    if (directionToCurrentCamera.lengthSq() < 0.001) {
      // If camera is too close to target or at the target, create a default viewing vector.
      // This could be based on the avatar's presumed forward direction or a generic offset.
      // Example: view from slightly above and in front (assuming +Z is front for camera)
      directionToCurrentCamera.set(0, 0.1, 0.4); // Adjust this default offset as needed
    }
    directionToCurrentCamera.normalize();

    const desiredDistanceToHead = 0.45; // Adjust this value for how close the head zoom should be
    const targetCameraPos = new THREE.Vector3()
      .copy(newControlsTarget)
      .addScaledVector(directionToCurrentCamera, desiredDistanceToHead);

    animateCameraAndTarget(targetCameraPos, newControlsTarget, () => {
      if (mountedRef.current) currentZoomLevelRef.current = ZOOM_STATE_HEAD;
    });
  }, [animateCameraAndTarget]);

  const zoomToFeet = useCallback(() => {
    if (
      !mountedRef.current ||
      !avatarNodeRef.current ||
      !cameraRef.current ||
      !controlsRef.current ||
      isAnimatingZoomRef.current ||
      !isMobile
    )
      return;
    if (currentZoomLevelRef.current === ZOOM_STATE_FEET && !isAnimatingZoomRef.current) return; // Already at feet or just finished anim

    // Find the feet bones. Assuming LeftFoot or RightFoot exist for full body avatars.
    const leftFootBone = findNode(avatarNodeRef.current, n => n.isBone && n.name === "LeftFoot");
    const rightFootBone = findNode(avatarNodeRef.current, n => n.isBone && n.name === "RightFoot");

    if (!leftFootBone && !rightFootBone) {
      console.warn("Feet bones not found for zoom.");
      return;
    }

    // Use the midpoint between left and right foot if both exist, otherwise use the one that exists
    const feetPosition = new THREE.Vector3();
    if (leftFootBone && rightFootBone) {
      leftFootBone.getWorldPosition(feetBoneWorldPosRef.current);
      const rightFootPos = new THREE.Vector3();
      rightFootBone.getWorldPosition(rightFootPos);
      feetPosition.addVectors(feetBoneWorldPosRef.current, rightFootPos).multiplyScalar(0.5);
    } else if (leftFootBone) {
      leftFootBone.getWorldPosition(feetPosition);
    } else {
      rightFootBone.getWorldPosition(feetPosition);
    }
    const newControlsTarget = feetPosition.clone();
    newControlsTarget.y -= 0.05;
    // newControlsTarget.x -= 0.1;

    const currentCameraPos = cameraRef.current.position.clone();
    const directionToCurrentCamera = new THREE.Vector3().subVectors(currentCameraPos, newControlsTarget);

    if (directionToCurrentCamera.lengthSq() < 0.001) {
      // If camera is too close to target or at the target, create a default viewing vector.
      // This could be based on the avatar's presumed forward direction or a generic offset.
      // Example: view from slightly above and in front (assuming +Z is front for camera)
      directionToCurrentCamera.set(0, 0.1, 0.4); // Adjust this default offset as needed
    }
    directionToCurrentCamera.normalize();

    const desiredDistanceToFeet = 1.2; // Adjust this value for how close the feet zoom should be (Increased distance based on feedback)
    const targetCameraPos = new THREE.Vector3()
      .copy(newControlsTarget)
      .addScaledVector(directionToCurrentCamera, desiredDistanceToFeet);

    animateCameraAndTarget(targetCameraPos, newControlsTarget, () => {
      if (mountedRef.current) currentZoomLevelRef.current = ZOOM_STATE_FEET;
    });
  }, [animateCameraAndTarget]);

  const zoomToFullBody = useCallback(() => {
    if (
      !mountedRef.current ||
      !avatarNodeRef.current ||
      !cameraRef.current ||
      !controlsRef.current ||
      isAnimatingZoomRef.current
    )
      return;
    if (currentZoomLevelRef.current === ZOOM_STATE_FULL_BODY && !isAnimatingZoomRef.current) return;

    // Map contentType to utility ContentType enum
    const contentTypeMap = {
      avatar: ContentType.AVATAR,
      model: ContentType.MODEL,
      generic: ContentType.GENERIC
    };

    const mappedContentType = contentTypeMap[contentType] || ContentType.AVATAR;

    // Use model-viewer approach for models, traditional approach for avatars
    if (contentType === "avatar") {
      // Traditional avatar zoom (legacy behavior)
      const box = new THREE.Box3().setFromObject(avatarNodeRef.current);
      const center = new THREE.Vector3();
      box.getCenter(center);
      // Adjust center for better framing (often higher part of body)
      center.y = (box.max.y - box.min.y) * 0.6 + box.min.y; // Standard centering logic

      const targetControlsTarget = center.clone();

      // Calculate camera position using fitBoxInFrustum logic
      // Create a temporary camera for calculation to avoid modifying the actual camera's state prematurely
      const tempCam = cameraRef.current.clone();
      fitBoxInFrustum(tempCam, box, targetControlsTarget, DEFAULT_MARGIN); // fitBoxInFrustum modifies tempCam.position
      const targetCameraPosition = tempCam.position.clone();

      animateCameraAndTarget(targetCameraPosition, targetControlsTarget, () => {
        if (mountedRef.current) currentZoomLevelRef.current = ZOOM_STATE_FULL_BODY;
      });
    } else {
      // Use model-viewer approach for models
      const result = calculateCameraFraming(avatarNodeRef.current, cameraRef.current, {
        contentType: mappedContentType,
        applyToCamera: false // Don't apply yet, we want to animate
      });

      animateCameraAndTarget(result.position, result.target, () => {
        if (mountedRef.current) currentZoomLevelRef.current = ZOOM_STATE_FULL_BODY;
      });
    }
  }, [animateCameraAndTarget, contentType]); // fitBoxInFrustum is stable if not dependent on changing refs/props

  // Mount/Unmount Effect
  useEffect(() => {
    mountedRef.current = true;
    if (!canvasDomRef.current) return;

    if (!roomEnvironmentRef.current) {
      roomEnvironmentRef.current = new RoomEnvironment();
    }

    sceneRef.current = new THREE.Scene();
    sceneRef.current.background = null;

    // Create studio lighting — enable real-time shadows only if preference is on
    const enableRealTimeShadows = window.APP?.store?.state?.preferences?.enableDynamicShadows === true;
    studioLightingRef.current = createStudioLighting(enableRealTimeShadows);
    sceneRef.current.add(studioLightingRef.current);

    // Create shadow receiver plane for real-time shadows
    shadowReceiverRef.current = createShadowReceiver();
    sceneRef.current.add(shadowReceiverRef.current);

    // Create throttled shadow updater for performance
    const keyLight = getKeyLight(studioLightingRef.current);
    if (keyLight && enableRealTimeShadows) {
      updateShadowRef.current = createThrottledShadowUpdater(keyLight, 100);
    }

    cameraRef.current = new THREE.PerspectiveCamera(
      55,
      canvasDomRef.current.clientWidth / canvasDomRef.current.clientHeight || 1,
      0.1,
      1000
    );
    controlsRef.current = new OrbitControls(cameraRef.current, canvasDomRef.current);
    controlsRef.current.screenSpacePanning = true;

    controlsRef.current.minDistance = 0.5;
    controlsRef.current.maxDistance = 5;

    const handleControlsEnd = () => {
      if (
        !mountedRef.current ||
        !isFullbodyRef.current ||
        isAnimatingZoomRef.current ||
        !controlsRef.current ||
        !cameraRef.current ||
        !avatarNodeRef.current
      ) {
        return;
      }

      // Current distance from camera to target
      const distance = cameraRef.current.position.distanceTo(controlsRef.current.target);

      // Thresholds (these will need tuning)
      const HEAD_MAX_DISTANCE_FOR_HEAD_VIEW = 0.7; // If in head view and distance > this, zoom to full body
      const FULL_BODY_MIN_DISTANCE_FOR_HEAD_TRANSITION = 2; // If in full body and distance < this, zoom to head

      if (currentZoomLevelRef.current === ZOOM_STATE_FULL_BODY) {
        const boundingBox = new THREE.Box3().setFromObject(avatarNodeRef.current);
        const avatarHeight = boundingBox.max.y - boundingBox.min.y;
        // Adjust threshold based on avatar size or use a fixed value like FULL_BODY_MIN_DISTANCE_FOR_HEAD_TRANSITION
        const dynamicHeadZoomInThreshold = Math.min(avatarHeight * 0.6, FULL_BODY_MIN_DISTANCE_FOR_HEAD_TRANSITION);

        if (distance < dynamicHeadZoomInThreshold) {
          zoomToHead();
        }
      } else if (currentZoomLevelRef.current === ZOOM_STATE_HEAD) {
        if (distance > HEAD_MAX_DISTANCE_FOR_HEAD_VIEW) {
          zoomToFullBody();
        }
      } else if (currentZoomLevelRef.current === ZOOM_STATE_FEET) {
        // Define a threshold for zooming out from feet view
        // const FEET_MAX_DISTANCE_FOR_FEET_VIEW = 1.0; // Adjust this value
        // if (distance > FEET_MAX_DISTANCE_FOR_FEET_VIEW) {
        //   zoomToFullBody();
        // }
      }
    };

    if (controlsRef.current) {
      controlsRef.current.addEventListener("end", handleControlsEnd);
    }

    cameraRef.current.position.set(-0.2, 0.5, 0.5);
    cameraRef.current.matrixAutoUpdate = true;
    controlsRef.current.target.set(0, 0.45, 0);
    controlsRef.current.update();

    const clock = new THREE.Clock();

    snapshotCanvasRef.current = document.createElement("canvas");
    snapshotCanvasRef.current.width = 720;
    snapshotCanvasRef.current.height = 1280;
    snapshotCameraRef.current = new THREE.PerspectiveCamera(55, 720 / 1280, 0.1, 1000);
    snapshotCameraRef.current.matrixAutoUpdate = true;

    snapshotRendererRef.current = createRenderer(snapshotCanvasRef.current, true, false);
    snapshotRendererRef.current.toneMapping = THREE.LinearToneMapping;
    snapshotRendererRef.current.toneMappingExposure = 1;
    snapshotRendererRef.current.setClearColor(getThemeBackground(), 0);

    previewRendererRef.current = createRenderer(canvasDomRef.current, true);
    previewRendererRef.current.toneMapping = THREE.LinearToneMapping;
    previewRendererRef.current.toneMappingExposure = 1;
    // Enable shadow map only if the preference is on
    previewRendererRef.current.shadowMap.enabled = enableRealTimeShadows;
    previewRendererRef.current.shadowMap.type = THREE.PCFSoftShadowMap;

    previewRendererRef.current.setAnimationLoop(() => {
      const dt = clock.getDelta();
      if (mixerRef.current) mixerRef.current.update(dt);
      TWEEN.update();
      // Throttled shadow update — runs every frame but only sets needsUpdate at most every 100ms
      if (updateShadowRef.current) updateShadowRef.current();
      if (sceneRef.current && cameraRef.current) {
        previewRendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    });

    removeThemeChangedListenerRef.current = onThemeChanged(() => {
      if (snapshotRendererRef.current) {
        snapshotRendererRef.current.setClearColor(getThemeBackground(), 0);
      }
    });

    window.addEventListener("resize", handleResize);
    handleResize();

    // Add ResizeObserver for the outer container
    let resizeObserver;
    if (containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      customAnimationControllerRef.current?.stop();
      mountedRef.current = false;
      window.removeEventListener("resize", handleResize);
      if (removeThemeChangedListenerRef.current) removeThemeChangedListenerRef.current();

      if (resizeObserver) resizeObserver.disconnect();
      if (previewRendererRef.current) {
        previewRendererRef.current.setAnimationLoop(null);
        previewRendererRef.current.forceContextLoss();
        previewRendererRef.current.dispose();
      }
      if (snapshotRendererRef.current) {
        snapshotRendererRef.current.forceContextLoss();
        snapshotRendererRef.current.dispose();
      }
      if (controlsRef.current) controlsRef.current.dispose();

      if (envMapRenderTargetRef.current) {
        envMapRenderTargetRef.current.dispose();
        envMapRenderTargetRef.current = null;
      }

      // Dispose studio lighting
      disposeStudioLighting(studioLightingRef.current);
      studioLightingRef.current = null;

      // Dispose shadow receiver
      if (shadowReceiverRef.current) {
        shadowReceiverRef.current.geometry.dispose();
        shadowReceiverRef.current.material.dispose();
        sceneRef.current?.remove(shadowReceiverRef.current);
        shadowReceiverRef.current = null;
      }
      updateShadowRef.current = null;

      if (sceneRef.current) {
        if (sceneRef.current.background && sceneRef.current.background.isTexture) {
          sceneRef.current.background.dispose();
        }
        sceneRef.current.traverse(disposeNode);
      }

      Object.values(imageBitmapsRef.current).forEach(img => disposeImageBitmap(img));
      imageBitmapsRef.current = {};

      sceneRef.current = null;
      cameraRef.current = null;
      controlsRef.current = null;
      previewRendererRef.current = null;
      snapshotRendererRef.current = null;
      snapshotCameraRef.current = null;
      mixerRef.current = null;
      idleAnimationActionRef.current = null;
      avatarNodeRef.current = null;
      previewMeshRef.current = null;
      originalMapsRef.current = {};
    };
  }, [handleResize, zoomToFullBody, zoomToHead, zoomToFeet]);

  // Effect for avatarGltfUrl changes
  useEffect(() => {
    if (!mountedRef.current) return;
    customAnimationControllerRef.current?.stop();

    if (avatarNodeRef.current && sceneRef.current && !preserveCamera) {
      sceneRef.current.remove(avatarNodeRef.current);
      avatarNodeRef.current = null;
    }
    if (mixerRef.current && !preserveCamera) {
      mixerRef.current.stopAllAction();
      mixerRef.current = null;
    }
    if (!preserveCamera) {
      idleAnimationActionRef.current = null;
      previewMeshRef.current = null;
      originalMapsRef.current = {};
      // Dispose any textures created by applyMaps for the previous avatar
      Object.values(imageBitmapsRef.current).forEach(img => disposeImageBitmap(img));
      imageBitmapsRef.current = {};
    }

    if (avatarGltfUrl) {
      setError(null);
      if (!preserveCamera) {
        setLoading(true);
      }
      loadCurrentAvatarGltfUrl();
    } else {
      setLoading(false);
      setError(null);
    }
    // Do NOT reset camera here
  }, [avatarGltfUrl, preserveCamera, loadCurrentAvatarGltfUrl]);

  // Only position the camera the first time an avatar is loaded
  // useEffect(() => {
  //   if (!hasPositionedCamera.current && avatarNodeRef.current && cameraRef.current && controlsRef.current) {
  //     resetCamera();
  //     hasPositionedCamera.current = true;
  //   }
  // }, [resetCamera]);

  // Effect for map props changes
  useEffect(() => {
    // Ensure prevPropsRef is updated even if maps don't change but other props do.
    // This keeps prevPropsRef.current[mapName] accurate for the next comparison.
    const prevProps = prevPropsRef.current;
    prevPropsRef.current = props;

    if (!mountedRef.current || !previewMeshRef.current || !avatarNodeRef.current || loading) return;

    const oldMapProps = {};
    const newMapProps = {};
    let mapsChanged = false;

    ALL_MAPS.forEach(mapName => {
      oldMapProps[mapName] = prevProps[mapName]; // Use the captured prevProps for this render cycle
      newMapProps[mapName] = props[mapName];
      if (prevProps[mapName] !== props[mapName]) {
        mapsChanged = true;
      }
    });

    if (mapsChanged) {
      applyMaps(oldMapProps, newMapProps);
    }
    // Make sure all props that could affect map selection or are used by applyMaps are dependencies.
    // The individual map props ensure this effect runs on map changes.
    // `props` itself as a dependency helps keep `prevPropsRef` logic simple.
  }, [props.base_map, props.emissive_map, props.normal_map, props.orm_map, applyMaps, loading, props]);

  useImperativeHandle(ref, () => ({
    snapshot: () => {
      return new Promise(resolve => {
        if (
          !sceneRef.current ||
          !snapshotCameraRef.current ||
          !snapshotRendererRef.current ||
          !cameraRef.current ||
          !snapshotCanvasRef.current ||
          !roomEnvironmentRef.current
        ) {
          console.error("Snapshot prerequisites not met.");
          resolve(null);
          return;
        }
        if (idleAnimationActionRef.current) idleAnimationActionRef.current.stop();

        const originalBackground = sceneRef.current.background;
        const originalEnvironment = sceneRef.current.environment;
        sceneRef.current.background = null;

        // Regenerate environment map for the snapshot renderer's context
        let snapshotRenderTarget = null;
        try {
          const snapshotPMREMGenerator = new THREE.PMREMGenerator(snapshotRendererRef.current);
          snapshotRenderTarget = snapshotPMREMGenerator.fromScene(roomEnvironmentRef.current);
          sceneRef.current.environment = snapshotRenderTarget.texture;
          snapshotPMREMGenerator.dispose();
        } catch (e) {
          console.error("Error generating environment map for snapshot:", e);
          sceneRef.current.environment = originalEnvironment;
        }

        // Update materials to use the new environment map
        sceneRef.current.traverse(object => {
          if (object.isMesh && object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(mat => {
                mat.needsUpdate = true;
              });
            } else {
              object.material.needsUpdate = true;
            }
          }
        });

        snapshotCameraRef.current.position.copy(cameraRef.current.position);
        snapshotCameraRef.current.rotation.copy(cameraRef.current.rotation);
        snapshotCameraRef.current.aspect = snapshotCanvasRef.current.width / snapshotCanvasRef.current.height;
        snapshotCameraRef.current.updateProjectionMatrix();

        snapshotRendererRef.current.render(sceneRef.current, snapshotCameraRef.current);

        // Restore original scene state
        sceneRef.current.background = originalBackground;
        sceneRef.current.environment = originalEnvironment;

        // Dispose the temporary snapshot environment map's render target
        if (snapshotRenderTarget) {
          snapshotRenderTarget.dispose();
        }

        snapshotCanvasRef.current.toBlob(blob => {
          if (idleAnimationActionRef.current) idleAnimationActionRef.current.play();
          resolve(blob);
        }, "image/png");
      });
    },
    zoomToHead,
    zoomToFeet,
    resetZoom: zoomToFullBody,
    exportAvatar: async () => {
      if (!avatarNodeRef.current) {
        console.warn("No avatar loaded to export.");
        return null;
      }

      const report = animationCapabilitiesRef.current;
      const declaredEmbedded =
        !showAnimationProfileSelector &&
        avatarNodeRef.current.userData?.avatarCapabilities?.animations?.source === "embedded";
      if (showAnimationProfileSelector || declaredEmbedded) {
        const selection = animationSelectionRef.current;
        const metadata = declaredEmbedded
          ? avatarNodeRef.current.userData.avatarCapabilities
          : buildAvatarCapabilitiesMetadata(report, selection);
        if (!declaredEmbedded) {
          if (selection === ANIMATION_SELECTIONS.LEGACY_FEMALE || selection === ANIMATION_SELECTIONS.LEGACY_MALE) {
            setAnimationProfileMetadata(
              avatarNodeRef.current,
              isFullbodyRef.current,
              selection === ANIMATION_SELECTIONS.LEGACY_MALE ? "male" : "female"
            );
          } else {
            delete avatarNodeRef.current.userData.animationProfile;
            delete avatarNodeRef.current.userData.isMaleAnim;
            delete avatarNodeRef.current.userData.isFemaleAnim;
          }
          setAvatarCapabilitiesMetadata(avatarNodeRef.current, metadata);
        }

        const blob = await exportCustomAvatarAtRest(
          avatarNodeRef.current,
          declaredEmbedded
            ? { binary: true, animations: (report?.embedded?.allClips || []).slice() }
            : buildCustomAvatarExportOptions(report, selection),
          new GLTFExporter(),
          mixerRef.current,
          () => (mountedRef.current ? runAnimationFullBody() : undefined)
        );
        const expectedClipNames =
          declaredEmbedded || selection === ANIMATION_SELECTIONS.EMBEDDED ? report?.embedded?.allClipNames || [] : [];
        const verification = await verifyCustomAvatarBlobExport(blob, expectedClipNames, metadata);
        if (!verification.valid)
          throw new Error(`Custom avatar export verification failed: ${verification.errors.join(" ")}`);
        return blob;
      }

      if (animationCapabilitiesRef.current?.hasDeclaredAnimationSource) {
        return await exportAvatar(avatarNodeRef.current);
      }
      setAnimationProfileMetadata(avatarNodeRef.current, isFullbodyRef.current, animationProfileRef.current);
      return await exportAvatar(avatarNodeRef.current);
    },
    resetCamera: resetCamera
  }));

  const displayError = error || propHasError;
  const displayLoading = (!avatarGltfUrl || loading || isRefetching) && !displayError && !progress;
  const canvasHidden = progress || displayError || isRefetching || displayLoading;

  const applyChangesSelector = isAvatarListing && currentViewportWidth < 992;
  const showAnimationSelector = showAnimationProfileSelector && animationCapabilities && isFullbodyRef.current;
  const availableAnimationSelections = getAvailableAnimationSelections(animationCapabilities);

  const onAnimationSelectionChange = useCallback(
    (_event, data) => {
      const selection = data.optionValue;
      animationSelectionRef.current = selection;
      isFullbodyMaleRef.current = selection === ANIMATION_SELECTIONS.LEGACY_MALE;
      setAnimationSelection(selection);
      runAnimationFullBody(idleAnimationActionRef.current?.time || 0);
    },
    [runAnimationFullBody]
  );

  return (
    <div
      ref={containerRef}
      className={classNames(
        styles.preview,
        className,
        applyChangesSelector ? "fullscreen-preview" : "",
        currentViewportWidth < 992 && isAvatarListing && !isSelector ? "hide" : ""
      )}
    >
      {progress ? <ProgressBar progress={progress}></ProgressBar> : ""}
      {displayLoading && (
        <div className="loader-spinner">
          <Spinner appearance="primary" />
        </div>
      )}
      {avatarGltfUrl && displayError && (
        <div className="error">
          <img src={warningIconUrl} srcSet={`${warningIcon2xUrl} 2x`} className="error-icon" alt="" />
          <FormattedMessage
            id="avatar-preview.loading-failed"
            defaultMessage="Loading failed{linebreak}Please choose another avatar"
            values={{ linebreak: <br /> }}
          />
        </div>
      )}
      <canvas ref={canvasDomRef} className={canvasHidden ? "canvas-hidden" : ""} />
      {showAnimationSelector && (
        <div className={styles.animationProfile}>
          <Label htmlFor="avatar-animation-profile">
            <FormattedMessage id="avatar-preview.animationSource" defaultMessage="Animation source" />
          </Label>
          <Dropdown
            id="avatar-animation-profile"
            appearance="filled-darker"
            selectedOptions={[animationSelection]}
            value={animationSelectionLabel(intl, animationSelection)}
            onOptionSelect={onAnimationSelectionChange}
          >
            {availableAnimationSelections.map(selection => (
              <Option key={selection} value={selection}>
                {animationSelectionLabel(intl, selection)}
              </Option>
            ))}
          </Dropdown>
        </div>
      )}
      {avatarCreator && !isMobile && (
        <div className={styles.infoHelp}>
          <div className="info-help-item">
            <img src={MouseZoom} alt="Mouse zoom icon" />
            <p>
              <FormattedMessage id="avatar-preview.zoomHint" defaultMessage="Zoom in and out" />
            </p>
          </div>
          <div className="info-help-item">
            <img src={MouseRotate} alt="Mouse rotate icon" />
            <p>
              <FormattedMessage id="avatar-preview.rotateHint" defaultMessage="Rotate" />
            </p>
          </div>
        </div>
      )}
      {isSelector && (
        <div className={styles.buttonSelect}>
          <Button preset="accent4" onClick={onSelect} className="custom-avatar-button" type="button">
            <FormattedMessage id="avatar-preview.selectButton" defaultMessage="Select Avatar" />
          </Button>
        </div>
      )}
      {applyChangesSelector && onClose && (
        <div className="close-preview" onClick={onClose} role="button" tabIndex={0} aria-label="Close preview">
          <Close />
        </div>
      )}
      {children}
    </div>
  );
});

AvatarPreview.propTypes = {
  avatarGltfUrl: PropTypes.string,
  className: PropTypes.string,
  onGltfLoaded: PropTypes.func,
  progress: PropTypes.number,
  avatarCreator: PropTypes.bool,
  isSelector: PropTypes.bool,
  showAnimationProfileSelector: PropTypes.bool,
  onSelect: PropTypes.func,
  isAvatarListing: PropTypes.bool,
  onClose: PropTypes.func,
  hasError: PropTypes.bool,
  isRefetching: PropTypes.bool,
  base_map: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]),
  emissive_map: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]),
  normal_map: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]),
  orm_map: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]),
  intl: PropTypes.object.isRequired,
  children: PropTypes.node,
  contentType: PropTypes.oneOf(["avatar", "model", "generic"]), // Type of content being displayed
  preserveCamera: PropTypes.bool
};

AvatarPreview.displayName = "AvatarPreview";

export default injectIntl(AvatarPreview, { forwardRef: true });
